import { useEffect, useMemo, useState } from 'react';
import { CalendarRange, LoaderCircle } from 'lucide-react';
import { DayPicker, type DropdownProps } from 'react-day-picker';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Button } from '@shared/ui/primitives/Button';
import { Dropdown } from '@shared/ui/primitives/Dropdown';

const FULL_DATE_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

interface CalendarDatePickerFieldProps {
  label: string;
  description: string;
  helperText: string;
  selectedDate?: Date;
  visibleMonth: Date;
  startMonth?: Date;
  endMonth?: Date;
  isLoading?: boolean;
  hasAvailableDays: boolean;
  emptyMonthMessage: string;
  fallbackLabel?: string;
  confirmLabel?: string;
  enabledMonthKeys?: string[];
  isDateDisabled?: (date: Date) => boolean;
  onMonthChange: (month: Date) => void;
  onSelectDate: (date: Date) => void;
}

export function CalendarDatePickerField({
  label,
  description,
  helperText,
  selectedDate,
  visibleMonth,
  startMonth,
  endMonth,
  isLoading,
  hasAvailableDays,
  emptyMonthMessage,
  fallbackLabel = 'Select a date',
  confirmLabel = 'Confirm',
  enabledMonthKeys,
  isDateDisabled,
  onMonthChange,
  onSelectDate,
}: CalendarDatePickerFieldProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [dropdownYear, setDropdownYear] = useState(visibleMonth.getFullYear());
  const enabledMonthKeySet = useMemo(
    () => new Set(enabledMonthKeys ?? []),
    [enabledMonthKeys],
  );
  const enabledYearSet = useMemo(
    () => new Set((enabledMonthKeys ?? []).map((key) => key.slice(0, 4))),
    [enabledMonthKeys],
  );
  const hasEnabledMonthFilter = !!enabledMonthKeys;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
  };

  useEffect(() => {
    if (!open) return;
    setDropdownYear(visibleMonth.getFullYear());

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape, { capture: true });
    return () => window.removeEventListener('keydown', handleEscape, { capture: true });
  }, [open]);

  const CalendarDropdown = ({ options, className, components: _components, classNames, ...props }: DropdownProps): JSX.Element => {
    const selectClassName = [classNames.dropdown, className].filter(Boolean).join(' ');
    const isMonthDropdown = className?.includes('calendar-month-dropdown');
    const isYearDropdown = className?.includes('calendar-year-dropdown');

    return (
      <Dropdown
        {...props}
        className={selectClassName}
        onChange={(event) => {
          if (isYearDropdown) {
            setDropdownYear(Number(event.target.value));
          }
          props.onChange?.(event);
        }}
      >
        {options?.map(({ value, label, disabled }) => {
          const monthKey = `${dropdownYear}-${pad(value + 1)}`;
          const isUnavailableMonth =
            hasEnabledMonthFilter &&
            ((isMonthDropdown && !enabledMonthKeySet.has(monthKey)) ||
              (isYearDropdown && !enabledYearSet.has(String(value))));

          return (
            <option key={value} value={value} disabled={disabled || isUnavailableMonth}>
              {label}
            </option>
          );
        })}
      </Dropdown>
    );
  };

  return (
    <>
      <div>
        <p className="eyebrow mb-2.5">{label}</p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-h-12 w-full items-center justify-between gap-3 rounded-md border border-hairline-strong bg-elevated px-3.5 text-left transition-colors hover:bg-sunken"
        >
          <div className="min-w-0">
            <p className="text-body text-ink">
              {selectedDate ? FULL_DATE_FMT.format(selectedDate) : fallbackLabel}
            </p>
            <p className="mt-0.5 text-caption text-ink-muted">{helperText}</p>
          </div>
          <CalendarRange className="h-5 w-5 shrink-0 text-ink-muted" strokeWidth={1.75} />
        </button>
      </div>

      <BottomSheet
        open={open}
        onOpenChange={handleOpenChange}
        onDragDismiss={() => setOpen(false)}
        title={label}
        description={description}
      >
        <div className="pb-4">
          <div className="relative min-h-[21rem]">
            <DayPicker
              mode="single"
              month={visibleMonth}
              captionLayout="dropdown"
              hideNavigation
              startMonth={startMonth}
              endMonth={endMonth}
              selected={selectedDate}
              onMonthChange={onMonthChange}
              onSelect={(date) => {
                if (!date) return;
                if (isDateDisabled?.(date)) return;
                onSelectDate(date);
              }}
              disabled={isDateDisabled}
              components={{
                Dropdown: CalendarDropdown,
              }}
              classNames={{
                root: 'w-full',
                months: 'w-full',
                month: 'w-full',
                month_caption: 'mb-3 flex items-center justify-start',
                caption_label: 'hidden',
                chevron: 'hidden',
                dropdowns: 'flex items-center justify-start gap-2',
                dropdown_root: 'relative',
                dropdown:
                  'h-10 rounded-md border border-hairline-strong bg-card px-3 pr-8 text-body-sm font-medium text-ink focus:outline-none focus:ring-2 focus:ring-primary-600/20',
                months_dropdown: 'calendar-month-dropdown min-w-[8.5rem]',
                years_dropdown: 'calendar-year-dropdown min-w-[6rem]',
                month_grid: 'w-full border-separate border-spacing-y-1.5',
                weekdays: 'mb-1',
                weekday:
                  'text-center text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-ink-muted',
                week: 'w-full',
                day: 'p-0 text-center',
                day_button:
                  'mx-auto flex h-10 w-10 items-center justify-center rounded-sm text-body-sm font-medium text-ink transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-0',
                selected: '[&>button]:bg-primary-700 [&>button]:text-white [&>button]:hover:bg-primary-700',
                today: '[&>button]:border [&>button]:border-primary-200 [&>button]:rounded-sm',
                disabled: 'text-ink-muted opacity-35',
              }}
            />
            {isLoading ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-elevated/70 backdrop-blur-[1px]">
                <LoaderCircle className="h-6 w-6 animate-spin text-primary-700" strokeWidth={1.75} />
              </div>
            ) : null}
          </div>
          {!isLoading && !hasAvailableDays ? (
            <p className="mt-4 rounded-xl bg-sunken px-4 py-4 text-center text-body-sm text-ink-secondary">
              {emptyMonthMessage}
            </p>
          ) : null}
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              disabled={!selectedDate}
              onClick={() => setOpen(false)}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
